/**
 * appIntegrityService.ts
 * API client for the App Integrity validation suite (Admin → App Integrity).
 * Backend routes: /api/admin/integrity/* (admin only).
 */

import apiClient from './apiClient';

export interface IntegrityExpectation {
  IntegrityExpectationKey: string;
  IntegrityFixtureKey: string;
  ResultPath: string;
  Label: string;
  ExpectedValue: string;
  ToleranceAbs: number | null;
  ToleranceRel: number | null;
  Unit: string | null;
  SortOrder: number;
}

export interface IntegrityFileMeta {
  IntegrityFixtureFileKey: string;
  FileName: string;
  FileKind: string;
  contentLength?: number;
  Content?: string;
}

/** WHERE the fixture's golden data came from (CJ 2026-07-30) — stored as a
 *  per-fixture meta file, printed in the Formula Verification PDF. */
export interface FixtureProvenance {
  sourceProject?: string | null;
  sourceWorkOrder?: string | null;
  /** WorkOrderKey GUID — deep-links straight to the source work order. */
  sourceWorkOrderKey?: string | null;
  sourceDocument?: string | null;
  note?: string | null;
}

/** One golden re-baseline (append-only history; never edited). */
export interface GoldenHistoryEntry {
  at: string;
  by: string;
  justification: string;
  changedExpectations: Array<{ path: string; label: string; from: string; to: string }>;
  supersededChecksums: Array<{ scope: string; column: string; checksum: string; sum?: number | null }>;
}

export interface IntegrityFixture {
  IntegrityFixtureKey: string;
  FixtureName: string;
  AdapterId: string;
  TestName: string;
  SpecimenCount: number;
  InputPayload: string;
  Notes: string | null;
  IsActive: boolean;
  SortOrder: number;
  expectations: IntegrityExpectation[];
  files: IntegrityFileMeta[];
  /** Provenance: on the LIST (UI v2) and the detail. */
  provenance?: FixtureProvenance | null;
  /** True when a golden checksum baseline is pinned (list view, UI v2). */
  goldenPinned?: boolean;
  /** Present on GET /fixtures/:key (detail) only. */
  goldenHistory?: GoldenHistoryEntry[];
}

export interface IntegrityCheck {
  path: string;
  label: string;
  unit: string | null;
  expected: string;
  actual: string | null;
  delta: number | null;
  toleranceAbs: number | null;
  toleranceRel: number | null;
  pass: boolean;
}

export interface IntegrityRun {
  IntegrityRunKey: string;
  Status: 'running' | 'passed' | 'failed' | 'error' | 'cancelled';
  StartedAt: string;
  FinishedAt: string | null;
  TriggeredBy: string;
  Environment: string;
  AppVersion: string;
  FixtureCount: number;
  PassCount: number;
  FailCount: number;
  ErrorCount: number;
  Message: string | null;
}

/** Formula-registry link for one check (hover UI, Vahid 2026-07-22). */
export interface IntegrityFormulaLink {
  formulaLabel: string;
  formula?: string;
  note?: string;
  kind: 'calc' | 'raw';
}

export interface IntegrityRunResult {
  IntegrityRunResultKey: string;
  IntegrityFixtureKey: string;
  FixtureName: string;
  Status: 'passed' | 'failed' | 'error' | 'skipped' | 'running';
  DurationMs: number | null;
  Message: string | null;
  checks: IntegrityCheck[];
  /** Keyed by check path; absent when no registry match. */
  formulas?: Record<string, IntegrityFormulaLink>;
  /** Calculated-column digests (2026-07-24); absent for pre-feature runs. */
  columnChecksums?: IntegrityColumnChecksum[];
  /** Formulas whose registry text changed since the golden was pinned —
   *  populated on NON-passed results only (Tier 3A, 2026-07-30). */
  formulaChanges?: Array<{ columnKey: string; label: string; pinned: string; current: string }>;
}

/** One calculated column's data digest (see backend columnChecksums.ts —
 *  SHA-256 over the column's stored values in row order, first 16 hex). */
export interface IntegrityColumnChecksum {
  scope: string;
  column: string;
  label?: string;
  rows: number;
  /** Plain control total — sum of the column's numeric values (null for
   *  text columns); hand-re-derivable in Excel (CJ 2026-07-30). */
  sum: number | null;
  checksum: string;
  /** Pinned golden digest (absent when no golden pinned for this column). */
  golden?: string;
  /** The pinned golden's control total (absent for pre-2026-07-30 goldens). */
  goldenSum?: number | null;
  /** checksum === golden (only set when a golden exists). */
  matchesGolden?: boolean;
}

export interface IntegrityRunDetail {
  run: IntegrityRun;
  results: IntegrityRunResult[];
  progress: {
    completed: number;
    total: number;
    currentFixture: string | null;
  } | null;
}

export interface ExpectationDraft {
  resultPath: string;
  label: string;
  expectedValue: string;
  toleranceAbs: number | null;
  toleranceRel: number | null;
  unit: string | null;
  sortOrder: number;
}

export async function fetchIntegrityAdapters(): Promise<Array<{ id: string; label: string }>> {
  const response = await apiClient.get('/admin/integrity/adapters');
  return response.data;
}

export async function fetchFixtures(): Promise<{ fixtures: IntegrityFixture[]; runActive: boolean }> {
  const response = await apiClient.get('/admin/integrity/fixtures');
  return response.data;
}

export async function fetchFixture(key: string): Promise<IntegrityFixture> {
  const response = await apiClient.get(`/admin/integrity/fixtures/${key}`);
  return response.data;
}

export async function updateFixture(
  key: string,
  patch: Partial<{
    fixtureName: string;
    testName: string;
    specimenCount: number;
    inputPayload: unknown;
    notes: string | null;
    isActive: boolean;
    sortOrder: number;
    provenance: FixtureProvenance;
  }>,
): Promise<void> {
  await apiClient.put(`/admin/integrity/fixtures/${key}`, patch);
}

export async function replaceExpectations(
  key: string,
  expectations: ExpectationDraft[],
): Promise<IntegrityExpectation[]> {
  const response = await apiClient.put(`/admin/integrity/fixtures/${key}/expectations`, {
    expectations,
  });
  return response.data.expectations;
}

export async function deleteFixture(key: string): Promise<void> {
  await apiClient.delete(`/admin/integrity/fixtures/${key}`);
}

export async function importFromTest(input: {
  workOrderDetailKey: string;
  adapterId?: string;
  fixtureName?: string;
  notes?: string;
}): Promise<{ fixture: IntegrityFixture }> {
  const response = await apiClient.post('/admin/integrity/fixtures/import-from-test', input);
  return response.data;
}

/** Controlled golden re-baseline (CJ 2026-07-30): a written justification is
 *  REQUIRED; the superseded baseline is preserved in the fixture's
 *  append-only golden history. */
export async function updateSnapshot(
  key: string,
  justification: string,
): Promise<{ updated: number; checks: IntegrityCheck[]; expectations: IntegrityExpectation[] }> {
  const response = await apiClient.post(`/admin/integrity/fixtures/${key}/update-snapshot`, {
    justification,
  });
  return response.data;
}

export async function runSingleFixture(key: string): Promise<{
  checks: IntegrityCheck[];
  formulas?: Record<string, IntegrityFormulaLink>;
  columnChecksums?: IntegrityColumnChecksum[];
}> {
  const response = await apiClient.post(`/admin/integrity/fixtures/${key}/run`);
  return response.data;
}

export async function startRun(fixtureKeys?: string[]): Promise<string> {
  const response = await apiClient.post('/admin/integrity/runs', { fixtureKeys });
  return response.data.runKey;
}

export async function fetchRuns(): Promise<{ runs: IntegrityRun[]; runActive: boolean }> {
  const response = await apiClient.get('/admin/integrity/runs');
  return response.data;
}

export async function fetchRun(runKey: string): Promise<IntegrityRunDetail> {
  const response = await apiClient.get(`/admin/integrity/runs/${runKey}`);
  return response.data;
}

export async function exportBundle(): Promise<unknown> {
  const response = await apiClient.get('/admin/integrity/export');
  return response.data;
}

export async function importBundle(bundle: {
  fixtures: unknown[];
}): Promise<{ created: number; updated: number }> {
  const response = await apiClient.post('/admin/integrity/import', bundle);
  return response.data;
}

/** Scheduled-run settings (CJ 2026-07-30): weekly/monthly suite runs under
 *  the system identity, email ONLY on failure, watchdog for missed runs. */
export interface IntegrityScheduleSettings {
  enabled: boolean;
  cadence: 'weekly' | 'monthly';
  weekday: number;
  hourUtc: number;
  recipients: string[];
  nextDueAt: string | null;
  lastScheduledRun: {
    runKey: string;
    status: string;
    startedAt: string;
    passCount: number;
    fixtureCount: number;
  } | null;
  emailConfigured: boolean;
  internalJobConfigured: boolean;
}

export async function fetchScheduleSettings(): Promise<IntegrityScheduleSettings> {
  const response = await apiClient.get('/admin/integrity/schedule-settings');
  return response.data;
}

export async function saveScheduleSettings(patch: {
  enabled: boolean;
  cadence: 'weekly' | 'monthly';
  weekday: number;
  hourUtc: number;
  recipients: string[];
}): Promise<IntegrityScheduleSettings> {
  const response = await apiClient.put('/admin/integrity/schedule-settings', patch);
  return response.data;
}

/** Download the kit-rendered FORMULA VERIFICATION PDF for a run
 *  (Vahid 2026-07-22): every check next to the registry formula that
 *  produced it, with the house sign-off footer as approval lines. */
export async function downloadFormulaReport(runKey: string): Promise<void> {
  const res = await apiClient.get(`/admin/integrity/runs/${runKey}/formula-report`, {
    responseType: 'blob',
  });
  const url = URL.createObjectURL(res.data as Blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `Integrity-Formula-Report_${runKey.slice(0, 8)}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
}
